import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Compass, ShieldCheck, Tent } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { useLang } from "../i18n";

const ICONS = [Compass, ShieldCheck, Tent];

const WordReveal = ({ text }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.45"] });
  const words = text.split(" ");
  return (
    <p ref={ref} className="font-display text-3xl sm:text-4xl lg:text-6xl leading-[1.12] text-[#F7F4EE]">
      {words.map((w, i) => (
        <Word key={i} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]}>{w}</Word>
      ))}
    </p>
  );
};

const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.25, 1]);
  return <motion.span style={{ opacity }} className="inline-block mr-[0.25em]">{children}</motion.span>;
};

export const About = () => {
  const { t } = useLang();
  return (
    <section id="about" className="relative py-16 md:py-40 bg-[#14120E] overflow-hidden" data-testid="about-section">
      <div className="absolute -right-40 top-20 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px] pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10">
        <Reveal><SectionLabel index="05">{t.about.label}</SectionLabel></Reveal>
        <div className="mt-10 max-w-6xl"><WordReveal key={t.about.title} text={t.about.title} /></div>
        <div className="mt-16 md:mt-24 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <div className="relative aspect-[3/4] overflow-hidden rounded-t-full border border-[#D4AF37]/25">
              <img src="/img/real1.jpg" alt="TSM WAY Sahara" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-6 flex flex-col justify-end">
            <Reveal><p className="text-base sm:text-lg text-[#CFC6B6] leading-relaxed max-w-xl">{t.about.text}</p></Reveal>
            <div className="mt-12 grid sm:grid-cols-3 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15">
              {t.about.values.map(([h, p], i) => {
                const Icon = ICONS[i];
                return (
                  <Reveal key={h} delay={i * 0.1} className="bg-[#14120E]">
                    <div className="p-6 md:p-8 h-full group" data-testid={`about-value-${i}`}>
                      <Icon className="text-[#D4AF37] transition-transform duration-500 group-hover:rotate-12" size={26} strokeWidth={1.3} />
                      <h3 className="mt-6 font-display text-2xl text-[#F7F4EE]">{h}</h3>
                      <p className="mt-2 text-sm text-[#B5AC9C] leading-relaxed">{p}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-10 flex items-center gap-6" data-testid="about-partner">
                <img src="/img/partner.jpg" alt="Baroudeurs de Desert" className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border border-[#D4AF37]/40" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">{t.about.partner}</p>
                  <p className="mt-2 font-display text-2xl text-[#F7F4EE]">Baroudeurs de Desert</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
